import { getThemeColors } from "../lib/theme";
import { useTranslation } from "react-i18next";
import { ProviderBadge } from "./ProviderBadge";
import { ProfileAvatar } from "./ProfileAvatar";

interface PodiumPlayer {
  id: string;
  name: string;
  /** Provider of the player's most recent sign-in, rendered as the badge. */
  provider?: string | null;
  image?: string | null;
  targetsFound: number;
}

interface PodiumProps {
  players: PodiumPlayer[];
  isDark: boolean;
  className?: string;
  currentPlayerId?: string | null;
}

export function Podium({
  players,
  isDark,
  className = "",
  currentPlayerId,
}: PodiumProps) {
  const theme = getThemeColors(isDark);
  const { t } = useTranslation();

  // Take first 3 players (already sorted from backend)
  const sortedPlayers = players.slice(0, 3);

  if (sortedPlayers.length === 0) return null;

  const first = sortedPlayers[0];
  const second = sortedPlayers[1];
  const third = sortedPlayers[2];

  // Heights for the podium bars
  const getHeight = (position: number) => {
    switch (position) {
      case 0:
        return "h-32"; // 1st place - tallest
      case 1:
        return "h-24"; // 2nd place
      case 2:
        return "h-16"; // 3rd place
      default:
        return "h-12";
    }
  };

  const getMedal = (position: number) => {
    switch (position) {
      case 0:
        return "🥇";
      case 1:
        return "🥈";
      case 2:
        return "🥉";
      default:
        return "";
    }
  };

  const renderPlace = (player: PodiumPlayer | undefined, position: number) => {
    if (!player) return null;

    const isCurrentPlayer = player.id === currentPlayerId;
    const heightClass = getHeight(position);
    const medal = getMedal(position);

    return (
      <div className="flex flex-col items-center">
        {/* Player Avatar */}
        <div className={`relative mb-3 ${position === 0 ? "scale-110" : ""}`}>
          <ProfileAvatar image={player.image} name={player.name} />
          <span className="absolute -bottom-1 -right-1 text-xl">{medal}</span>
        </div>

        {/* Player Name */}
        <div className="text-center mb-2 px-2 w-28">
          <div
            className={`flex flex-col items-center gap-1 font-semibold text-sm leading-tight ${isCurrentPlayer ? theme.text : theme.textMuted}`}
          >
            <span>
              {player.name.length > 18
                ? player.name.slice(0, 18) + "..."
                : player.name}
            </span>
            <ProviderBadge provider={player.provider} />
          </div>
          {isCurrentPlayer && (
            <div className={`text-xs mt-1 ${theme.textMuted}`}>
              {t("common.you")}
            </div>
          )}
        </div>

        {/* Podium Bar */}
        <div
          className={`w-28 ${heightClass} rounded-t-lg flex items-end justify-center pb-2 ${
            position === 0
              ? isDark
                ? "bg-zinc-700"
                : "bg-gray-300"
              : position === 1
                ? isDark
                  ? "bg-zinc-800"
                  : "bg-gray-200"
                : isDark
                  ? "bg-zinc-900"
                  : "bg-gray-100"
          }`}
        >
          <span
            className={`text-xs font-bold ${position === 0 ? theme.text : theme.textMuted}`}
          >
            {position + 1}
            {getOrdinal(position + 1)}
          </span>
        </div>
      </div>
    );
  };

  const getOrdinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
  };

  return (
    <div className={`flex justify-center items-end gap-3 px-4 ${className}`}>
      {/* 2nd Place */}
      {second && <div className="order-1">{renderPlace(second, 1)}</div>}

      {/* 1st Place */}
      {first && <div className="order-2 -mt-4">{renderPlace(first, 0)}</div>}

      {/* 3rd Place */}
      {third && <div className="order-3">{renderPlace(third, 2)}</div>}
    </div>
  );
}
